import { useQuery } from 'react-query'
import { gridClient } from '@/utils/apiUtil'

export const VIEW_AS_CUSTOMERS_QUERY_KEY = 'view-as-customers'

/** Matches the backend `ViewAsCustomer` returned by GET /auth/view-as/customers. */
export type ViewAsCustomer = {
  id: string;
  name: string;
  parentId: string | null;
}

/** Matches the backend `ViewAsScope`: Grid staff see every customer, parent-org Admins their sub-organizations. */
export const VIEW_AS_SCOPE = {
  All: 'all',
  SubOrganizations: 'subOrganizations',
} as const

export type ViewAsScope = typeof VIEW_AS_SCOPE[keyof typeof VIEW_AS_SCOPE]

/** The `viewAs` field on your own GET /user/:id profile; absent when you cannot view as anyone. */
export type ViewAsCapability = {
  scope: ViewAsScope;
  demoCustomer?: { id: string; name: string };
}

type ViewAsTokens = {
  bearerToken: string;
  refreshToken: string;
}

/**
 * @returns every customer the signed-in user may view as, sorted by name
 */
export const getViewAsCustomersRequest = async (): Promise<ViewAsCustomer[]> => {
  const { data } = await gridClient.get<ViewAsCustomer[]>('/auth/view-as/customers')
  return data
}

/**
 * @param enabled fetch only once the switcher opens
 */
export function getViewAsCustomersHook({ enabled }: { enabled: boolean }) {
  return useQuery(VIEW_AS_CUSTOMERS_QUERY_KEY, getViewAsCustomersRequest, {
    enabled,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * @param customerId the customer to view as
 * @returns a token pair whose effective customer is `customerId`
 */
export const enterViewAsRequest = async (customerId: string): Promise<ViewAsTokens> => {
  const { data } = await gridClient.post<ViewAsTokens>(`/auth/view-as/${customerId}`)
  return data
}

/**
 * @returns a token pair for the user's home customer
 */
export const exitViewAsRequest = async (): Promise<ViewAsTokens> => {
  const { data } = await gridClient.delete<ViewAsTokens>('/auth/view-as')
  return data
}
